var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useEffect, useCallback } from "react";
import isEqual from "lodash/isEqual";
import { useHistory, useLocation } from "react-router-dom";
import { locksCollectionPublisherToOwn } from "../utils/permissionsUtils";
import { usePublisherAutoFill } from "./usePublisherAutoFill";
import { usePublisherBySourceId } from "./usePublisherBySourceId";
var INITIAL_FILTERS = {
    type: "",
    name: "",
    publisher: "",
    issn: "",
};
/**
 * The applied filters as the URL holds them.
 *
 * @param search `location.search` of the list
 */
var filtersFromSearch = function (search) {
    var params = new URLSearchParams(search);
    return {
        type: params.get("type") || "",
        name: params.get("name") || "",
        publisher: params.get("publisher") || "",
        issn: params.get("issn") || "",
    };
};
var hasAnySearchParam = function (search) {
    return Array.from(new URLSearchParams(search).keys()).length > 0;
};
export var useCollectionsFilters = function () {
    var history = useHistory();
    var location = useLocation();
    var initialFilters = INITIAL_FILTERS;
    // Initialised from the URL in the first render: filters patched in by an
    // effect afterwards would change the search and send it twice.
    var _a = useState(function () {
        return filtersFromSearch(location.search);
    }), filters = _a[0], setFilters = _a[1];
    var _b = useState(filters.type), temporaryTypeFilter = _b[0], setTemporaryTypeFilter = _b[1];
    var _c = useState(filters.name), temporaryNameFilter = _c[0], setTemporaryNameFilter = _c[1];
    var _d = useState(filters.publisher), temporaryPublisherFilter = _d[0], setTemporaryPublisherFilter = _d[1];
    var _e = useState(filters.issn), temporaryIssnFilter = _e[0], setTemporaryIssnFilter = _e[1];
    var _f = useState(""), publisherSearchQuery = _f[0], setPublisherSearchQuery = _f[1];
    var _g = usePublisherBySourceId(temporaryPublisherFilter || undefined), selectedPublisherData = _g.data, selectedPublisherLoading = _g.isLoading;
    var updateUrlWithFilters = useCallback(function (newFilters) {
        var searchParams = new URLSearchParams();
        if (newFilters.type)
            searchParams.set("type", newFilters.type);
        if (newFilters.name)
            searchParams.set("name", newFilters.name);
        if (newFilters.publisher)
            searchParams.set("publisher", newFilters.publisher);
        if (newFilters.issn)
            searchParams.set("issn", newFilters.issn);
        history.push({ search: searchParams.toString() });
    }, [history]);
    // A list opened without URL params shows a publisher user their own collections.
    var autoFill = usePublisherAutoFill({
        appliesTo: locksCollectionPublisherToOwn,
        hasFilterParams: hasAnySearchParam,
        canAutoFill: function () { return !temporaryPublisherFilter; },
        onAutoFill: function (publisherSourceId) {
            var autoFilledFilters = __assign(__assign({}, INITIAL_FILTERS), { publisher: publisherSourceId });
            setFilters(autoFilledFilters);
            setTemporaryPublisherFilter(publisherSourceId);
            updateUrlWithFilters(autoFilledFilters);
        },
    });
    var handleApplyFilters = function () {
        var publisherSourceId = temporaryPublisherFilter && (selectedPublisherData === null || selectedPublisherData === void 0 ? void 0 : selectedPublisherData.sourceId)
            ? selectedPublisherData.sourceId
            : temporaryPublisherFilter;
        var newFilters = {
            type: temporaryTypeFilter,
            name: temporaryNameFilter,
            publisher: publisherSourceId,
            issn: temporaryIssnFilter,
        };
        autoFill.cancel();
        setFilters(newFilters);
        updateUrlWithFilters(newFilters);
    };
    var handleClearFilters = function () {
        setTemporaryTypeFilter("");
        setTemporaryNameFilter("");
        setTemporaryPublisherFilter("");
        setTemporaryIssnFilter("");
        autoFill.cancel();
        setFilters(initialFilters);
        history.push({ search: "" });
    };
    var handlePublisherSearchInput = function (inputValue) {
        if (inputValue !== publisherSearchQuery) {
            setPublisherSearchQuery(inputValue);
        }
    };
    var handlePublisherSelection = function (selectedId) {
        setTemporaryPublisherFilter(selectedId);
    };
    var isAnyFilterActive = function () {
        return !!(filters.type ||
            filters.name ||
            filters.publisher ||
            filters.issn);
    };
    // Back/forward or a pasted URL: the URL is the applied filters and replaces the draft.
    useEffect(function () {
        var filtersFromUrl = filtersFromSearch(location.search);
        setFilters(function (prev) {
            return isEqual(prev, filtersFromUrl) ? prev : filtersFromUrl;
        });
        setTemporaryTypeFilter(filtersFromUrl.type);
        setTemporaryNameFilter(filtersFromUrl.name);
        setTemporaryPublisherFilter(filtersFromUrl.publisher);
        setTemporaryIssnFilter(filtersFromUrl.issn);
    }, [location.search]);
    /** False until the publisher auto-fill is decided — a search before it would be thrown away. */
    var filtersReady = !autoFill.isPending;
    return {
        filters: filters,
        filtersReady: filtersReady,
        /** true when the logged-in user or their roles could not be loaded */
        currentUserLoadFailed: autoFill.isError,
        temporaryTypeFilter: temporaryTypeFilter,
        temporaryNameFilter: temporaryNameFilter,
        temporaryPublisherFilter: temporaryPublisherFilter,
        temporaryIssnFilter: temporaryIssnFilter,
        publisherSearchQuery: publisherSearchQuery,
        selectedPublisherData: selectedPublisherData,
        selectedPublisherLoading: selectedPublisherLoading,
        setTemporaryTypeFilter: setTemporaryTypeFilter,
        setTemporaryNameFilter: setTemporaryNameFilter,
        setTemporaryPublisherFilter: setTemporaryPublisherFilter,
        setTemporaryIssnFilter: setTemporaryIssnFilter,
        handleApplyFilters: handleApplyFilters,
        handleClearFilters: handleClearFilters,
        handlePublisherSearchInput: handlePublisherSearchInput,
        handlePublisherSelection: handlePublisherSelection,
        isAnyFilterActive: isAnyFilterActive,
    };
};
